/**
 * Per-assistant visual identity — shared by the Hub grid, the Hub ask-bar,
 * and anywhere else an assistant needs to look like itself.
 *
 * Two things live here:
 *
 *   1. `iconOf` — the operator's `settings.icon`, falling back to the
 *      built-in default for that slug (so shipped assistants show their
 *      icons without a backend reseed).
 *   2. `assistantTint` — a stable accent colour per assistant.
 *
 * Why tints: six assistants rendered on the *same* brand gradient read as
 * six copies of the logo, so nothing is distinguishable at a glance and the
 * badge competes with the header wordmark. Each assistant instead gets one
 * of six hues that are all `color-mix`ed *from* `--color-accent`, so the
 * palette re-derives itself under any white-label brand — a navy tenant
 * gets a navy-family spread, a crimson tenant a crimson-family spread — and
 * nothing here needs a per-tenant override. The companions are the same
 * Nordic set the aurora tokens use (teal / indigo / gold / rose / green /
 * sky), so the Hub stays inside the product's existing palette.
 *
 * The hue is keyed on the slug, not the grid position, so an assistant
 * keeps its colour when the list is reordered or filtered.
 */
import type { Assistant } from "./api/conversations";
import { DEFAULT_ASSISTANT_ICONS } from "./assistant-icons";

/** Companions blended with the brand accent, in assignment order. */
const TINT_COMPANIONS = [
  "#1fb6a6", // teal
  "#7b6fe0", // indigo
  "#e0a44a", // gold
  "#d9748c", // rose
  "#5aa06f", // green
  "#4f90d4", // sky
] as const;

/** How much brand accent survives the mix. Higher = more uniform, but a
 * near-black accent (DOIT's #0B2A4A) crushes the companion into grey long
 * before it reaches half, so this stays low enough to keep each hue
 * readable as a hue. */
const ACCENT_SHARE = 26;

/** djb2 — small, stable, and dependency-free. Order-independent by design. */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * A CSS colour for `slug`, expressed as a `color-mix()` against the live
 * `--color-accent` so it tracks the tenant's brand at runtime. Feed it into
 * a `--tint` custom property on the element.
 */
export function assistantTint(slug: string): string {
  const companion =
    TINT_COMPANIONS[hash(slug || "default") % TINT_COMPANIONS.length];
  return `color-mix(in oklab, var(--color-accent) ${ACCENT_SHARE}%, ${companion})`;
}

/**
 * Icon name for an assistant: the operator's `settings.icon` when set,
 * otherwise the built-in default for that slug. Unlisted slugs return
 * undefined, which callers render as a name monogram.
 */
export function iconOf(a: Assistant): string | undefined {
  const v = a.settings?.icon;
  if (typeof v === "string" && v.trim()) return v;
  return DEFAULT_ASSISTANT_ICONS[a.slug];
}

/** Operator's sample prompts from `settings.samples`, normalised to strings. */
export function samplesOf(a: Assistant): string[] {
  const raw = a.settings?.samples;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      const t = entry.trim();
      if (t) out.push(t);
    } else if (entry && typeof entry === "object") {
      const e = entry as { text?: unknown; enabled?: unknown };
      if (e.enabled === false) continue;
      const t = typeof e.text === "string" ? e.text.trim() : "";
      if (t) out.push(t);
    }
  }
  return out;
}

/**
 * Is this sample answerable exactly as written?
 *
 * Operator samples serve two jobs. Inside a chat they are templates you edit
 * before sending — "Forbered mig til mødet med <kunde>" is a perfectly good
 * prompt once you replace the placeholder. On the Hub they are one-click
 * triggers that route straight into the assistant and submit immediately, so
 * a template arrives at the model with the angle brackets still in it and
 * the assistant is asked about a customer literally named "<kunde>".
 *
 * This filters the Hub strip down to the samples that stand on their own.
 * It catches syntactic placeholders only — `<kunde>`, `[customer]`,
 * `{project}`, `XX`. A sample can still require input without announcing it
 * ("Here are my meeting notes — write minutes"), which no regex will spot;
 * see the note in AssistantHub about making this an explicit operator choice.
 */
export function isSelfContained(text: string): boolean {
  const PLACEHOLDER = [
    /<[^<>]*[A-Za-zÀ-ÿ][^<>]*>/, // <kunde>, <customer>
    /\[[^\[\]]*[A-Za-zÀ-ÿ][^\[\]]*\]/, // [customer]
    /\{[^{}]*[A-Za-zÀ-ÿ][^{}]*\}/, // {project}
    /\bX{2,}\b/i, // XX, XXX
  ];
  return !PLACEHOLDER.some((re) => re.test(text));
}
